export interface ActiveImageTask {
  taskId: string;
  elementType: string;
  elementName: string;
}

export function activeTaskKey(elementType: string, elementName: string): string {
  return `${elementType}:${elementName}`;
}

export function buildActiveImageTaskMap(
  tasks: ActiveImageTask[],
  resolvedTaskIds?: ReadonlySet<string>,
): Map<string, string> {
  const map = new Map<string, string>();
  tasks.forEach(task => {
    if (!task.taskId || resolvedTaskIds?.has(task.taskId)) return;
    map.set(activeTaskKey(task.elementType, task.elementName), task.taskId);
  });
  return map;
}

export interface FailedImageTask {
  taskId: string;
  elementType: string;
  elementName: string;
  error?: string;
  failureStage?: 'persist';
}

export interface SlotFailure {
  taskId: string;
  error?: string;
  failureStage?: 'persist';
}

export function buildFailedTaskMap(failed: FailedImageTask[]): Map<string, SlotFailure> {
  const map = new Map<string, SlotFailure>();
  failed.forEach(task => {
    if (!task.taskId) return;
    const failure: SlotFailure = {
      taskId: task.taskId,
      error: task.error,
    };
    if (task.failureStage) failure.failureStage = task.failureStage;
    map.set(activeTaskKey(task.elementType, task.elementName), failure);
  });
  return map;
}

// list 版：供“状态图多候选并行”使用，每个 canonical slot key 聚合一组任务条目。
// base 图仍使用上面的标量版（buildActiveImageTaskMap / buildFailedTaskMap），不受影响。

export function buildActiveImageTaskListMap(
  tasks: ActiveImageTask[],
  resolvedTaskIds?: ReadonlySet<string>,
): Map<string, ActiveImageTask[]> {
  const map = new Map<string, ActiveImageTask[]>();
  tasks.forEach(task => {
    if (!task.taskId || resolvedTaskIds?.has(task.taskId)) return;
    const key = activeTaskKey(task.elementType, task.elementName);
    const list = map.get(key) ?? [];
    list.push(task);
    map.set(key, list);
  });
  return map;
}

export function buildFailedTaskListMap(
  failed: FailedImageTask[],
  resolvedTaskIds?: ReadonlySet<string>,
): Map<string, SlotFailure[]> {
  const map = new Map<string, SlotFailure[]>();
  failed.forEach(task => {
    if (!task.taskId || resolvedTaskIds?.has(task.taskId)) return; // 已手动消除的失败不再显示
    const key = activeTaskKey(task.elementType, task.elementName);
    const list = map.get(key) ?? [];
    const failure: SlotFailure = { taskId: task.taskId, error: task.error };
    if (task.failureStage) failure.failureStage = task.failureStage;
    list.push(failure);
    map.set(key, list);
  });
  return map;
}
